import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Check, Mic, Square, RotateCcw, Play, ChevronLeft, Pause, Hand, Ear, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RolePlay, { PartRolePlay } from "@/components/RolePlay";
import ChatRolePlay, { CopyFlags, FullChats, PickQuiz } from "@/components/ChatRolePlay";
import NutritionBasics from "@/components/NutritionBasics";
import {
  getPractice, submitPracticeAttempt, getPracticeAttempt, getPracticeAudio, retryPracticeAttempt, overridePracticeTone,
  getPracticeTeam, getPracticeRegionScript, savePracticePart, resetPracticePart, savePracticeSettings,
} from "@/lib/api";

// Script practice: the rep reads one part of the official script into the phone, following the
// pause and "wait for the yes" markers, and FPA scores it out of 100. Level 1 shows the whole
// script, level 2 only the key phrases, level 3 nothing (from memory). Facility heads see their
// team and can set the tone score by ear; the trainer edits the script and the pass mark.

const LEVELS = { 1: "Read it", 2: "Prompts only", 3: "From memory", 4: "Role play", 5: "Hard role play" };
const SHORT = { 1: "Read", 2: "Prompts", 3: "Memory", 4: "Role play", 5: "Hard" };
const COACH_SHORT = { 1: "Pick", 2: "Best of 4", 3: "Write", 4: "Chat", 5: "Hard chat" };
const CORE_UNITS = 8;                 // coach units 9-12 (OARS) open once 1-8 are done
const PART_LABELS = { script: "Sticking to the script", pauses: "Pauses", pace: "Pace", tone: "Tone", fillers: "Filler words" };
const MARK_ICON = { pause: Pause, yes: Hand, listen: Ear };
const VERDICT = {
  pass: { label: "Passed", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  retake: { label: "Retake", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  rewatch: { label: "Rewatch, then retake", cls: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
};
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const clock = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]
  .find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported?.(m)) || "";
const extFor = (mime) => (mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm");

// "**key** words [pause] more\n" → [{t:"key", s:"..."}, {t:"text"}, {t:"mark", kind}, {t:"br"}]
function tokens(text) {
  const out = [];
  const re = /(\*\*[\s\S]+?\*\*|\[(?:pause|yes|listen)\]|\n)/gi;
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index > last) out.push({ t: "text", s: text.slice(last, m.index) });
    const v = m[0];
    if (v === "\n") out.push({ t: "br" });
    else if (v.startsWith("**")) out.push({ t: "key", s: v.slice(2, -2) });
    else out.push({ t: "mark", kind: v.slice(1, -1).toLowerCase() });
    last = m.index + v.length;
  }
  if (last < text.length) out.push({ t: "text", s: text.slice(last) });
  return out;
}

const Marker = ({ kind, markers }) => {
  const Icon = MARK_ICON[kind] || Pause;
  const m = markers.find((x) => x.kind === kind);
  return (
    <span className={`inline-flex items-center gap-1 align-middle mx-1 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${
      kind === "yes" ? "bg-blue-500/15 text-blue-300 border-blue-500/30" : kind === "listen" ? "bg-violet-500/15 text-violet-300 border-violet-500/30"
        : "bg-zinc-700/40 text-zinc-300 border-zinc-600"}`}>
      <Icon className="h-3 w-3" />{m?.label || kind}
    </span>
  );
};

function Prompter({ text, level, markers }) {
  const toks = useMemo(() => tokens(text), [text]);
  if (level === 3) {
    const marks = toks.filter((t) => t.t === "mark");
    return (
      <div className="text-zinc-400 text-base leading-relaxed">
        <p className="text-lg text-zinc-200 font-semibold">From memory.</p>
        <p className="mt-2">Deliver the whole part without looking. It has {marks.length} stops; take each one the way you practised.</p>
      </div>
    );
  }
  if (level === 2) {
    // Prompts only: the first few words of each line, then the stop markers. The rep says the whole line.
    const out = [];
    tokens(text.replaceAll("**", "")).forEach((t, i) => {
      if (t.t === "br") out.push(<br key={i} />);
      else if (t.t === "mark") out.push(<Marker key={i} kind={t.kind} markers={markers} />);
      else {
        t.s.split(/(?<=[.?!:])\s+/).forEach((sent, j) => {
          const w = sent.trim().split(/\s+/).filter(Boolean);
          if (!w.length) return;
          out.push(<span key={`${i}-${j}`} className="text-white">{w.slice(0, 3).join(" ")}{w.length > 3 && <span className="text-zinc-600"> … </span>} </span>);
        });
      }
    });
    return <div className="text-xl sm:text-2xl leading-[1.7] sm:leading-[1.75]" data-testid="practice-prompter">{out}</div>;
  }
  const shown = [];
  toks.forEach((t, i) => {
    if (t.t === "br") shown.push(<br key={i} />);
    else if (t.t === "key") shown.push(<strong key={i} className="text-white font-semibold">{t.s}</strong>);
    else if (t.t === "mark") shown.push(<Marker key={i} kind={t.kind} markers={markers} />);
    else shown.push(<span key={i} className="text-zinc-300">{t.s}</span>);
  });
  return <div className="text-xl sm:text-2xl leading-[1.7] sm:leading-[1.75]" data-testid="practice-prompter">{shown}</div>;
}

function Bar({ label, got, max }) {
  const pct = Math.max(0, Math.min(100, (got / max) * 100));
  return (
    <div>
      <div className="flex justify-between text-sm"><span className="text-zinc-300">{label}</span>
        <span className="tabular-nums text-zinc-400">{got} / {max}</span></div>
      <div className="h-2 rounded-full bg-zinc-800 mt-1 overflow-hidden">
        <div className={`h-full rounded-full ${pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function AudioPlayer({ id }) {
  const [url, setUrl] = useState(null);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (url) return <audio src={url} controls className="w-full" />;
  return (
    <Button type="button" variant="outline" size="sm" className="rounded-full"
      onClick={() => getPracticeAudio(id).then((b) => setUrl(URL.createObjectURL(b))).catch((e) => err(e, "Couldn't load the audio"))}>
      <Play className="h-4 w-4 mr-1" />Listen back
    </Button>
  );
}

function Result({ attempt, weights, onAgain, onNext, onBack, canOverride, onChanged }) {
  const [tone, setTone] = useState("");
  if (attempt.status === "error") {
    return (
      <div className="space-y-3">
        <p className="text-rose-300">Scoring didn't work: {attempt.error}</p>
        <Button onClick={() => retryPracticeAttempt(attempt.id).then(onChanged).catch((e) => err(e, "Couldn't retry"))}>Try scoring again</Button>
      </div>
    );
  }
  const d = attempt.details || {};
  const v = VERDICT[attempt.verdict] || VERDICT.rewatch;
  return (
    <div className="space-y-5" data-testid="practice-result">
      <div className="flex flex-wrap items-end gap-4">
        <div className="font-display text-5xl font-bold tabular-nums">{attempt.score}<span className="text-xl text-zinc-500">/100</span></div>
        <span className={`px-3 py-1 rounded-full border text-sm font-semibold ${v.cls}`}>{v.label}</span>
        <span className="text-sm text-zinc-500">Pass mark {attempt.pass_mark}</span>
      </div>
      {attempt.message && <p className="text-amber-300">{attempt.message}</p>}
      {attempt.verdict === "rewatch" && attempt.rewatch_session && (
        <p className="text-sm text-zinc-300">Watch the recording of live session {attempt.rewatch_session} on the <Link to="/training" className="underline">Sales Training</Link> page, then try this part again.</p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.keys(PART_LABELS).map((k) => <Bar key={k} label={PART_LABELS[k]} got={attempt.parts?.[k] ?? 0} max={weights[k]} />)}
      </div>
      {attempt.tone_override && <p className="text-xs text-zinc-500">Tone set by {attempt.tone_override.by} after listening (measured {attempt.tone_override.measured}).</p>}

      {!!d.pauses?.length && (
        <div>
          <div className="eyebrow mb-2">Your stops</div>
          <ul className="space-y-1.5 text-sm">
            {d.pauses.map((p, i) => (
              <li key={i} className="flex gap-2 items-start">
                {p.ok ? <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> : <span className="h-4 w-4 mt-0.5 shrink-0 text-rose-400 font-bold leading-4 text-center">!</span>}
                <span className="min-w-0"><span className="text-zinc-500">…{p.context}</span>{" "}
                  <span className="text-zinc-200">{p.label}: {p.got == null ? "not reached" : `${p.got}s`} (needs {p.need}s)</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!!d.missed_lines?.length && (
        <div>
          <div className="eyebrow mb-2">Lines skipped or changed</div>
          <ul className="list-disc pl-5 text-sm text-zinc-300 space-y-1">{d.missed_lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-3 text-sm">
        <div className="card-surface p-3"><div className="text-zinc-500 text-xs">Pace</div><div className="tabular-nums">{d.wpm ?? "–"} words a minute</div>
          <div className="text-xs text-zinc-500">Aim for {d.pace_good?.[0]}–{d.pace_good?.[1]}</div></div>
        <div className="card-surface p-3"><div className="text-zinc-500 text-xs">Filler words</div>
          <div>{d.fillers?.length ? d.fillers.map((f) => f.w).join(", ") : "None heard"}</div></div>
        <div className="card-surface p-3"><div className="text-zinc-500 text-xs">Tone (rough)</div>
          <div className="tabular-nums">Pitch range {d.tone?.pitch_sd ?? "–"} · loudness {d.tone?.loudness_sd ?? "–"}</div>
          <div className="text-xs text-zinc-500">Low numbers sound flat</div></div>
      </div>
      {attempt.has_audio && <AudioPlayer id={attempt.id} />}
      {attempt.transcript && (
        <details className="text-sm"><summary className="cursor-pointer text-zinc-400">What FPA heard</summary>
          <p className="mt-2 text-zinc-300 leading-relaxed">{attempt.transcript}</p></details>
      )}
      {canOverride && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-zinc-400">Set tone by ear (0–{weights.tone}):</span>
          <Input value={tone} onChange={(e) => setTone(e.target.value)} className="w-20 h-9" inputMode="decimal" data-testid="tone-input" />
          <Button size="sm" variant="outline" onClick={() => {
            const n = Number(tone);
            if (Number.isNaN(n) || n < 0 || n > weights.tone) return toast.error(`Enter 0 to ${weights.tone}`);
            overridePracticeTone(attempt.id, n, "").then(() => { toast.success("Tone saved"); onChanged(); }).catch((e) => err(e, "Couldn't save"));
          }}>Save tone</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {onAgain && <Button onClick={onAgain} className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Try again</Button>}
        {onNext && <Button onClick={onNext} className="rounded-full bg-emerald-600 hover:bg-emerald-500">Next level</Button>}
        {onBack && <Button variant="outline" onClick={onBack} className="rounded-full">All parts</Button>}
      </div>
    </div>
  );
}

function Session({ part, level, data, onDone, onExit }) {
  const [phase, setPhase] = useState("ready");      // ready | countdown | recording | uploading | scoring | result
  const [count, setCount] = useState(3);
  const [secs, setSecs] = useState(0);
  const [progress, setProgress] = useState(0);
  const [attempt, setAttempt] = useState(null);
  const rec = useRef(null), stream = useRef(null), chunks = useRef([]), mime = useRef(""), started = useRef(0), timer = useRef(null), wake = useRef(null);

  const cleanup = () => {
    clearInterval(timer.current);
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    try { wake.current?.release(); } catch { /* ignore */ }
  };
  useEffect(() => cleanup, []);

  const poll = useCallback((id) => {
    let tries = 0;
    const tick = () => getPracticeAttempt(id).then((a) => {
      if (a.status === "done" || a.status === "error" || ++tries > 90) { setAttempt(a); setPhase("result"); onDone(); }
      else setTimeout(tick, 2000);
    }).catch(() => setTimeout(tick, 4000));
    tick();
  }, [onDone]);

  const begin = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return toast.error("This browser can't record audio. Update your phone's browser.");
    try {
      // No auto gain: it flattens the voice and would hide how the rep lifts and drops it.
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false } });
    } catch { return toast.error("FPA needs microphone access. Allow it in the browser's site settings and try again."); }
    try { wake.current = await navigator.wakeLock?.request("screen"); } catch { /* not supported */ }
    setPhase("countdown");
    let n = 3;
    setCount(n);
    const cd = setInterval(() => {
      n -= 1;
      if (n > 0) { setCount(n); return; }
      clearInterval(cd);
      mime.current = pickMime();
      const r = new MediaRecorder(stream.current, { ...(mime.current ? { mimeType: mime.current } : {}), audioBitsPerSecond: 48000 });
      mime.current = r.mimeType || mime.current || "audio/webm";
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data?.size) chunks.current.push(e.data); };
      r.onstop = upload;
      rec.current = r;
      r.start(1000);
      started.current = Date.now();
      setSecs(0);
      timer.current = setInterval(() => setSecs((Date.now() - started.current) / 1000), 250);
      setPhase("recording");
    }, 800);
  };

  const upload = async () => {
    const seconds = (Date.now() - started.current) / 1000;
    cleanup();
    const blob = new Blob(chunks.current, { type: mime.current });
    if (!blob.size) { toast.error("No audio was captured. Check the microphone and try again."); setPhase("ready"); return; }
    setPhase("uploading");
    try {
      const file = new File([blob], `practice.${extFor(mime.current)}`, { type: mime.current });
      const a = await submitPracticeAttempt(part.n, level, Math.round(seconds), file, setProgress, data.track || "consult");
      setPhase("scoring");
      poll(a.id);
    } catch (e) { err(e, "Couldn't upload the recording"); setPhase("ready"); }
  };

  const stop = () => { if (rec.current && rec.current.state !== "inactive") rec.current.stop(); };
  const cancel = () => {
    if (rec.current && rec.current.state !== "inactive") { rec.current.onstop = null; rec.current.stop(); }
    cleanup();
    setPhase("ready");
  };

  const prog = data.progress[String(part.n)] || {};
  const passedNow = attempt?.status === "done" && attempt.passed;
  const nextLevel = passedNow && level < 5 ? level + 1 : null;

  return (
    <div className="space-y-4" data-testid="practice-session">
      <button type="button" onClick={() => { cancel(); onExit(); }} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200">
        <ChevronLeft className="h-4 w-4" />All parts
      </button>
      <section className="card-surface p-5 sm:p-7">
        <div className="eyebrow">Part {part.n} · Level {level}: {LEVELS[level]}</div>
        <h2 className="font-display text-xl sm:text-2xl font-bold mt-1">{part.title}</h2>
        {prog[String(level)] && <p className="text-xs text-zinc-500 mt-1">Best so far {prog[String(level)].best} · {prog[String(level)].attempts} attempt{prog[String(level)].attempts === 1 ? "" : "s"}</p>}

        {phase === "result" && attempt ? (
          <div className="mt-5">
            <Result attempt={attempt} weights={data.weights}
              onAgain={() => { setAttempt(null); setPhase("ready"); }}
              onNext={nextLevel ? () => onExit(nextLevel) : null}
              onBack={() => onExit()} onChanged={() => poll(attempt.id)} />
          </div>
        ) : (
          <>
            <div className="mt-5"><Prompter text={part.text} level={level} markers={data.markers} /></div>
            <div className="sticky bottom-0 mt-6 -mx-5 sm:-mx-7 px-5 sm:px-7 py-4 bg-[#0f0f0f]/95 border-t border-[#262626] flex flex-wrap items-center gap-3"
              style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))" }}>
              {phase === "ready" && <Button onClick={begin} className="rounded-full bg-rose-600 hover:bg-rose-500 px-6" data-testid="practice-start">
                <Mic className="h-4 w-4 mr-2" />Start reading</Button>}
              {phase === "countdown" && <div className="font-display text-3xl font-bold tabular-nums">{count}</div>}
              {phase === "recording" && (<>
                <span className="flex items-center gap-2 text-rose-300"><span className="h-2.5 w-2.5 rounded-full bg-rose-500 animate-pulse" />
                  <span className="tabular-nums">{clock(secs)}</span></span>
                <Button onClick={stop} className="rounded-full px-6" data-testid="practice-stop"><Square className="h-4 w-4 mr-2" />Stop and score</Button>
                <Button variant="ghost" onClick={cancel} className="rounded-full text-zinc-400">Cancel</Button>
              </>)}
              {phase === "uploading" && <span className="flex items-center gap-2 text-zinc-300"><Loader2 className="h-4 w-4 animate-spin" />Uploading {progress}%</span>}
              {phase === "scoring" && <span className="flex items-center gap-2 text-zinc-300"><Loader2 className="h-4 w-4 animate-spin" />Scoring your reading. This takes about 20 seconds.</span>}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

const dueDay = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

function Due({ label, due, done, late }) {
  return (
    <span className={`text-xs ${done ? "text-emerald-300" : late ? "text-rose-300" : "text-zinc-400"}`}>
      {done ? "✓ " : late ? "Overdue · " : ""}{label} {done ? "done" : `by ${dueDay(due)}`}
    </span>
  );
}

function Timeline({ timeline, parts }) {
  const title = Object.fromEntries(parts.map((p) => [p.n, p.title]));
  const late = timeline.overdue.length;
  return (
    <section className="card-surface p-5 sm:p-6 space-y-3" data-testid="practice-timeline">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div><div className="eyebrow">Your timeline</div>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">Each part is due in two steps after the online session that teaches it: say it (levels 1 to 3) before the next session, and its role play (level 4) within a week. The full consult role play is due by {dueDay(timeline.full_due)}.</p></div>
        <span className={`text-sm font-semibold ${late ? "text-rose-300" : "text-emerald-300"}`}>{late ? `${late} part${late > 1 ? "s" : ""} behind` : "On track"}</span>
      </div>
      <ul className="divide-y divide-[#1f1f1f]">
        {timeline.parts.map((r) => (
          <li key={r.part} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="w-52 shrink-0 text-zinc-200">Part {r.part} · {title[r.part]}</span>
            <span className="text-xs text-zinc-500 w-32">Taught {dueDay(r.taught)}</span>
            <Due label="Say it" due={r.read_due} done={r.read_done} late={r.read_overdue} />
            <Due label="Role play" due={r.roleplay_due} done={r.roleplay_done} late={r.roleplay_overdue} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function PartCard({ part, prog, passMark, onStart, coach = false, locked: unitLocked = false }) {
  const open = unitLocked ? 0 : prog?.open || 1;
  const short = coach ? COACH_SHORT : SHORT;
  return (
    <div className="card-surface p-4 flex flex-col gap-3" data-testid={`practice-part-${part.n}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0"><div className="eyebrow">{coach ? "Unit" : "Part"} {part.n}{coach && part.n > CORE_UNITS ? " · OARS" : ""}</div><div className="font-semibold mt-0.5">{part.title}</div></div>
        {prog?.done && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-300 whitespace-nowrap"><Award className="h-4 w-4" />Done</span>}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {[1, 2, 3, 4, 5].map((lv) => {
          const s = prog?.[String(lv)];
          const locked = lv > open;
          return (
            <button key={lv} type="button" disabled={locked} onClick={() => onStart(part, lv)}
              className={`rounded-lg border px-2 py-2 text-left transition ${locked ? "border-[#1f1f1f] text-zinc-600 cursor-not-allowed"
                : s?.passed ? "border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20" : "border-[#2a2a2a] hover:border-zinc-500"}`}>
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1 truncate">{locked && <Lock className="h-3 w-3 shrink-0" />}{lv} · {short[lv]}</div>
              <div className="text-sm font-semibold tabular-nums">{s ? s.best : locked ? "–" : "Start"}</div>
            </button>
          );
        })}
      </div>
      <div className="text-xs text-zinc-500">{unitLocked ? `Opens once units 1 to ${CORE_UNITS} are done`
        : coach && !prog?.done ? (open <= 2 ? `Get 4 of 5 right on level ${open} to open level ${open + 1}`
          : open === 3 ? `Write one reply and score ${passMark}+ to open the practice chat` : `Chat with the member and score ${passMark}+ to finish the unit`)
        : coach && prog?.done && !prog?.mastered ? `Done. Level 5 is a tough member: score ${passMark}+ to master it (needed to certify)`
        : prog?.mastered ? "Mastered. Keep practising to stay sharp"
        : prog?.done ? `Done. Level 5 is a tough member: score ${passMark}+ to master it`
          : open <= 3 ? `Say the whole part and score ${passMark}+ on level ${open} to open level ${open + 1}`
            : `Role play this part and score ${passMark}+ to finish it`}</div>
    </div>
  );
}

function Team({ data }) {
  const track = data.track || "consult";
  const [team, setTeam] = useState(null);
  const [allGyms, setAllGyms] = useState(false);
  const [open, setOpen] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const load = useCallback(() => getPracticeTeam(allGyms, track).then(setTeam).catch((e) => err(e, "Couldn't load the team")), [allGyms, track]);
  useEffect(() => { load(); }, [load]);
  if (!team) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  const best = (p) => { for (const lv of ["5", "4", "3", "2", "1"]) if (p?.[lv]?.passed) return `L${lv} ✓`; const any = ["1", "2", "3", "4", "5"].find((lv) => p?.[lv]); return any ? `L${any} ${p[any].best}` : "–"; };
  return (
    <div className="space-y-3">
      {data.role === "trainer" && track === "consult" && <label className="flex items-center gap-2 text-sm text-zinc-400"><input type="checkbox" checked={allGyms} onChange={(e) => setAllGyms(e.target.checked)} />All gyms</label>}
      {!team.rows.length && <p className="text-sm text-zinc-500">Nobody at this gym has practised yet.</p>}
      {!!team.rows.length && (
        <div className="overflow-x-auto card-surface">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="p-3">Rep</th>{data.parts.map((p) => <th key={p.n} className="p-3 text-center">P{p.n}</th>)}<th className="p-3">Done</th><th className="p-3">Behind</th><th className="p-3">Tries</th><th className="p-3">{track === "nurture" ? "Full calls" : "Role plays"}</th></tr></thead>
            <tbody>
              {team.rows.map((r) => (
                <tr key={r.user_id} className="border-t border-[#1f1f1f] hover:bg-white/[0.02] cursor-pointer" onClick={() => setOpen(open === r.user_id ? null : r.user_id)}>
                  <td className="p-3"><div className="font-medium">{r.name}</div>{allGyms && <div className="text-xs text-zinc-500">{r.gym}</div>}</td>
                  {data.parts.map((p) => <td key={p.n} className={`p-3 text-center tabular-nums ${r.progress[String(p.n)]?.done ? "text-emerald-300" : "text-zinc-300"}`}>{best(r.progress[String(p.n)])}</td>)}
                  <td className="p-3 tabular-nums">{r.parts_done}/{data.parts.length}</td>
                  <td className={`p-3 tabular-nums ${r.overdue?.length ? "text-rose-300" : "text-zinc-500"}`}>{r.overdue?.length ? `${r.overdue.length} parts` : "–"}</td>
                  <td className="p-3 tabular-nums">{r.attempts}</td>
                  <td className="p-3 tabular-nums whitespace-nowrap">{r.roleplays?.count ? `${r.roleplays.count} · best ${r.roleplays.best ?? "–"}` : "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && (() => {
        const r = team.rows.find((x) => x.user_id === open);
        return (
          <div className="card-surface p-4 space-y-2">
            <div className="font-semibold">{r.name}: latest attempts</div>
            {r.roleplays?.recent?.map((p) => (
              <div key={p.id} className="flex flex-wrap justify-between gap-2 text-sm border-t border-[#1f1f1f] pt-2">
                <span>Role play · {p.persona_name} ({p.difficulty}) · {new Date(p.started_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>
                <span className="tabular-nums">{p.score != null ? `${p.score}/100` : "not rated"}</span>
              </div>
            ))}
            {r.recent.map((a) => (
              <button key={a.id} type="button" className="w-full text-left flex flex-wrap justify-between gap-2 text-sm border-t border-[#1f1f1f] pt-2"
                onClick={() => getPracticeAttempt(a.id).then(setAttempt).catch((e) => err(e, "Couldn't open it"))}>
                <span>Part {a.part} · L{a.level} · {new Date(a.created_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>
                <span className="tabular-nums">{a.status === "done" ? `${a.score}/100` : a.status}</span>
              </button>
            ))}
          </div>
        );
      })()}
      {attempt && (
        <div className="card-surface p-5">
          <div className="eyebrow mb-3">Part {attempt.part} · Level {attempt.level} · {attempt.user_name}</div>
          <Result attempt={attempt} weights={data.weights} canOverride
            onBack={() => setAttempt(null)} onChanged={() => { getPracticeAttempt(attempt.id).then(setAttempt); load(); }} />
        </div>
      )}
    </div>
  );
}

function Editor({ data, reload }) {
  const nurture = data.track === "nurture";
  const [region, setRegion] = useState(data.region);
  const [parts, setParts] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [pass, setPass] = useState(String(data.pass_mark));
  useEffect(() => { getPracticeRegionScript(region).then((r) => { setParts(r.parts); setDrafts({}); }).catch((e) => err(e, "Couldn't load")); }, [region]);
  return (
    <div className="space-y-4">
      {data.role === "trainer" && <div className="card-surface p-4 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-zinc-300">Pass mark</span>
        <Input value={pass} onChange={(e) => setPass(e.target.value)} className="w-20 h-9" inputMode="numeric" />
        <Button size="sm" variant="outline" onClick={() => savePracticeSettings(Number(pass)).then(() => { toast.success("Pass mark saved"); reload(); }).catch((e) => err(e, "Use 50 to 95"))}>Save</Button>
        <span className="text-zinc-500">10 below the pass mark means retake; lower means rewatch the session first. Applies to every script.</span>
      </div>}
      {!nurture && <div className="flex gap-2">{["India", "Sri Lanka", "Dubai"].map((r) => (
        <Button key={r} size="sm" variant={r === region ? "default" : "outline"} className="rounded-full" onClick={() => setRegion(r)}>{r}</Button>))}</div>}
      <p className="text-sm text-zinc-400">Markers: <code>[pause]</code> 1s, <code>[yes]</code> wait for the yes 1.5s, <code>[listen]</code> let them answer 2.5s. Wrap key phrases in <code>**double stars**</code>; level 2 shows only those.</p>
      {!parts ? <Loader2 className="h-4 w-4 animate-spin text-zinc-500" /> : parts.map((p) => (
        <div key={p.n} className="card-surface p-4 space-y-2">
          <div className="font-semibold">Part {p.n}: {p.title}</div>
          <Textarea rows={8} value={drafts[p.n] ?? p.text} onChange={(e) => setDrafts({ ...drafts, [p.n]: e.target.value })} className="font-mono text-sm" />
          <div className="flex gap-2">
            <Button size="sm" disabled={drafts[p.n] == null} onClick={() => savePracticePart(region, p.n, { text: drafts[p.n] })
              .then((r) => { setParts(r.parts); setDrafts({ ...drafts, [p.n]: undefined }); toast.success("Saved"); reload(); }).catch((e) => err(e, "Couldn't save"))}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => resetPracticePart(region, p.n).then((r) => { setParts(r.parts); toast.success("Back to the official text"); reload(); })}>Reset to official text</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// track "consult": the Script Practice page (official sales script, per region).
// track "nurture": the Lead Nurture board's Practice tab (the Lead Nurture call script), embedded in that page.
export default function ScriptPractice({ track = "consult" }) {
  const nurture = track === "nurture";
  const coach = track === "accountability";
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(track === "accountability" ? "basics" : "practise");
  const [unitsOpen, setUnitsOpen] = useState(track !== "accountability");
  const [session, setSession] = useState(null);     // {part, level}
  const load = useCallback(() => getPractice(track).then(setData).catch((e) => err(e, "Couldn't load script practice")), [track]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;

  if (session && session.level <= 2 && coach) {
    return <PickQuiz key={`${session.part.n}-${session.level}`} part={session.part.n} level={session.level} title={session.part.title}
      passMark={data.pass_mark} onExit={(next) => { load(); setSession(next ? { part: session.part, level: next } : null); }} />;
  }
  if (session && session.level >= 3 && coach) {
    return <ChatRolePlay key={`${session.part.n}-${session.level}`} part={session.part.n} level={session.level} title={session.part.title}
      passMark={data.pass_mark} onExit={(next) => { load(); setSession(next ? { part: session.part, level: next } : null); }} />;
  }
  if (session && session.level >= 4) {
    return <PartRolePlay key={`${session.part.n}-${session.level}`} part={session.part} level={session.level} passMark={data.pass_mark} track={track}
      onExit={(next) => { load(); setSession(next ? { part: session.part, level: next } : null); }} />;
  }
  if (session) {
    return <Session key={`${session.part.n}-${session.level}`} part={session.part} level={session.level} data={data} onDone={load}
      onExit={(next) => setSession(next ? { part: session.part, level: next } : null)} />;
  }
  const isManager = data.role === "manager" || data.role === "trainer";
  const done = data.parts.filter((p) => data.progress[String(p.n)]?.done).length;
  const coreDone = data.parts.filter((p) => p.n <= CORE_UNITS && data.progress[String(p.n)]?.done).length;
  const tabs = [...(coach ? [["basics", "Nutrition basics"]] : []), ["practise", coach ? "Units" : nurture ? "Parts" : "Practise"], ["roleplay", coach ? "Certification" : nurture ? "Full call" : "Role play"], ["mine", "My attempts"],
    ...(isManager ? [["team", "Team"]] : []), ...((data.role === "trainer" && !coach) || (nurture && isManager) ? [["script", nurture ? "Script" : "Script and pass mark"]] : [])];

  return (
    <div className="space-y-5" data-testid="practice-page">
      <section className="card-surface p-5 sm:p-6">
        <div className="eyebrow">{coach ? "Accountability coach course" : nurture ? "Call practice · Lead Nurture script" : `Self-training · ${data.region} script`}</div>
        <h2 className="font-display text-xl sm:text-2xl font-bold mt-1">{coach ? "Coach practice" : nurture ? "Lead call practice" : "Script practice"}</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">{coach
          ? `Coaching Conversations: count before you write, one ask, and a reply that could only go to them. Then OARS: open questions, affirm the effort, reflect and ask before you advise, and summarise. Each unit has five levels: pick the right reply, pick the best of four close ones, write one reply, then a practice chat with a member and a hard one. Units 9 to 12 open once 1 to 8 are done. Master all ${data.parts.length} and pass the five-card set to become a LIFTR Certified Nutrition & Accountability Coach.`
          : nurture
          ? `Practise the Lead Nurture script one part at a time. Say each part out loud, stopping at every marker; FPA scores you out of 100 and you pass at ${data.pass_mark}. Each part has five levels: read it, prompts only, from memory, then a call with an AI lead for that part and a hard one. Finish all six parts to unlock the full call.`
          : `Read a part of the official script out loud into your phone. Stop at every marker. FPA scores you out of 100 and you pass at ${data.pass_mark}. Each part has five levels: read it, prompts only, from memory, then a role play of that part and a hard one. Finish every part to unlock the full consult role play.`}</p>
        {!coach && <div className="flex flex-wrap gap-2 mt-3">{data.markers.map((m) => (
          <span key={m.kind} className="text-xs text-zinc-400 flex items-center"><Marker kind={m.kind} markers={data.markers} />at least {m.seconds}s</span>))}</div>}
        <div className="text-sm text-zinc-300 mt-3"><span className="tabular-nums font-semibold">{done}/{data.parts.length}</span> {coach ? "units" : "parts"} done</div>
        {!coach && !data.transcription_ready && <p className="text-sm text-amber-300 mt-2">Scoring isn't switched on on the server yet; recordings will wait.</p>}
      </section>

      <div className="flex gap-1 overflow-x-auto">{tabs.map(([k, l]) => (
        <button key={k} type="button" onClick={() => setTab(k)}
          className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${tab === k ? "bg-white text-black font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>))}</div>

      {tab === "basics" && <NutritionBasics onUnitsOpen={() => setUnitsOpen(true)} />}
      {tab === "practise" && coach && !unitsOpen && (
        <p className="text-sm text-amber-300">The reply units open once you've passed all six Nutrition basics quizzes.
          <button type="button" className="underline ml-1" onClick={() => setTab("basics")}>Go to Nutrition basics</button></p>
      )}
      {tab === "practise" && data.timeline && <Timeline timeline={data.timeline} parts={data.parts} />}
      {tab === "practise" && (unitsOpen || !coach) && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {data.parts.map((p) => <PartCard key={p.n} part={p} prog={data.progress[String(p.n)]} passMark={data.pass_mark} coach={coach}
            locked={coach && p.n > CORE_UNITS && !isManager && coreDone < CORE_UNITS}
            onStart={(part, level) => setSession({ part, level })} />)}
        </div>
      )}
      {tab === "mine" && (
        <div className="card-surface divide-y divide-[#1f1f1f]">
          {!data.recent.length && <p className="p-4 text-sm text-zinc-500">No attempts yet. Pick a part and start with level 1.</p>}
          {data.recent.map((a) => (
            <div key={a.id} className="p-3 flex flex-wrap justify-between gap-2 text-sm">
              <span>Part {a.part}: {a.part_title} · L{a.level}</span>
              <span className="tabular-nums text-zinc-400">{new Date(a.created_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                {" · "}{a.status === "done" ? <span className={a.passed ? "text-emerald-300" : "text-zinc-200"}>{a.score}/100</span> : a.status}</span>
            </div>
          ))}
        </div>
      )}
      {tab === "roleplay" && (coach ? <FullChats /> : <RolePlay mode={nurture ? "nurture" : "consult"} />)}
      {tab === "team" && coach && <CopyFlags />}
      {tab === "team" && <Team data={data} />}
      {tab === "script" && <Editor data={data} reload={load} />}
    </div>
  );
}
